import type { Moment } from 'moment'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type { ItemCardVariant } from '../types'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { INTL_LONG_DATE, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import OutsideTripDatesIcon from 'tripPlanner/components/Itinerary/OutsideTripDatesIcon'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { Place } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'

const OutsideTripDatesContainer = styled.div`
  position: absolute;
  inset-inline-start: ${rem(76)};
  bottom: ${rem(-4)};
`
const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`

const StyledBodyText = styled(BodyText)`
  margin-inline-end: ${rem(16)};
`

type Props = {
  place: Place
  date: Moment | undefined
  time: Moment | undefined
  day?: number
  item: TripItem
  canShowWarning?: boolean
  variant: ItemCardVariant
}

export default function ItemLocationSummary({
  place,
  date,
  time,
  day,
  item,
  canShowWarning,
  variant,
}: Props) {
  const intl = useIntl()
  const openModal = useOpenModal()

  const editItem = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  return (
    <div>
      {day && <BodyText variant="small">{intl.formatMessage(
        {
          id: '7UZX7VsN58ZjEXsFisyN',
          defaultMessage: 'Day {day}',
          description: 'Day number label in trip itinerary item location summary',
        },
        { day },
      )}
      </BodyText>}
      {!day && date && (
        <DateContainer>
          <StyledBodyText variant="small">
            {intlDateFormatter(date, INTL_LONG_DATE, intl.locale)}
          </StyledBodyText>
          {canShowWarning && variant === 'tripSummary' && (
            <OutsideTripDatesContainer>
              <OutsideTripDatesIcon changeItemDates={editItem} item={item} />
            </OutsideTripDatesContainer>
          )}
        </DateContainer>
      )}
      <Caption variant="large" weight="bold">
        {place.name}
      </Caption>
      {time && <BodyText variant="small">{intlDateFormatter(time, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)}</BodyText>}
    </div>
  )
}
